import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Slider,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  RestartAlt as RestartAltIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SimpleProjectionAssumptions } from "../../../../types/SimpleProjection";

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 40;

interface ProjectionsSidebarProps {
  revenueGrowth: number;
  setRevenueGrowth: (value: number) => void;
  netIncomeMargin: number;
  setNetIncomeMargin: (value: number) => void;
  peRatio: number;
  setPeRatio: (value: number) => void;
  dilutionRate: number;
  setDilutionRate: (value: number) => void;
  assumptions?: SimpleProjectionAssumptions;
  onReset: () => void;
  isMobile?: boolean;
}

const ProjectionsSidebar: React.FC<ProjectionsSidebarProps> = ({
  revenueGrowth,
  setRevenueGrowth,
  netIncomeMargin,
  setNetIncomeMargin,
  peRatio,
  setPeRatio,
  dilutionRate,
  setDilutionRate,
  assumptions,
  onReset,
  isMobile = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Edit states
  const [editingRevenueGrowth, setEditingRevenueGrowth] = useState(false);
  const [editingNetIncomeMargin, setEditingNetIncomeMargin] = useState(false);
  const [editingPeRatio, setEditingPeRatio] = useState(false);
  const [editingDilution, setEditingDilution] = useState(false);

  // Input values
  const [revenueGrowthInput, setRevenueGrowthInput] = useState("");
  const [netIncomeMarginInput, setNetIncomeMarginInput] = useState("");
  const [peRatioInput, setPeRatioInput] = useState("");
  const [dilutionInput, setDilutionInput] = useState("");

  // Refs for auto-focus
  const revenueGrowthInputRef = useRef<HTMLInputElement>(null);
  const netIncomeMarginInputRef = useRef<HTMLInputElement>(null);
  const peRatioInputRef = useRef<HTMLInputElement>(null);
  const dilutionInputRef = useRef<HTMLInputElement>(null);

  const handleStartEditRevenueGrowth = () => {
    setRevenueGrowthInput(((revenueGrowth ?? 0) * 100).toFixed(1));
    setEditingRevenueGrowth(true);
  };

  const handleStartEditNetIncomeMargin = () => {
    setNetIncomeMarginInput(((netIncomeMargin ?? 0) * 100).toFixed(1));
    setEditingNetIncomeMargin(true);
  };

  const handleStartEditPeRatio = () => {
    setPeRatioInput((peRatio ?? 15).toFixed(1));
    setEditingPeRatio(true);
  };

  const handleStartEditDilution = () => {
    setDilutionInput(((dilutionRate ?? 0) * 100).toFixed(1));
    setEditingDilution(true);
  };

  const handleSaveRevenueGrowth = () => {
    const parsed = parseFloat(revenueGrowthInput);
    if (!isNaN(parsed) && parsed >= -10 && parsed <= 200) {
      setRevenueGrowth(parsed / 100);
    }
    setEditingRevenueGrowth(false);
  };

  const handleSaveNetIncomeMargin = () => {
    const parsed = parseFloat(netIncomeMarginInput);
    if (!isNaN(parsed) && parsed >= -20 && parsed <= 100) {
      setNetIncomeMargin(parsed / 100);
    }
    setEditingNetIncomeMargin(false);
  };

  const handleSavePeRatio = () => {
    const parsed = parseFloat(peRatioInput);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 100) {
      setPeRatio(parsed);
    }
    setEditingPeRatio(false);
  };

  const handleSaveDilution = () => {
    const parsed = parseFloat(dilutionInput);
    if (!isNaN(parsed) && parsed >= -20 && parsed <= 30) {
      setDilutionRate(parsed / 100);
    }
    setEditingDilution(false);
  };

  useEffect(() => {
    if (editingRevenueGrowth && revenueGrowthInputRef.current) {
      revenueGrowthInputRef.current.focus();
      revenueGrowthInputRef.current.select();
    }
  }, [editingRevenueGrowth]);

  useEffect(() => {
    if (editingNetIncomeMargin && netIncomeMarginInputRef.current) {
      netIncomeMarginInputRef.current.focus();
      netIncomeMarginInputRef.current.select();
    }
  }, [editingNetIncomeMargin]);

  useEffect(() => {
    if (editingPeRatio && peRatioInputRef.current) {
      peRatioInputRef.current.focus();
      peRatioInputRef.current.select();
    }
  }, [editingPeRatio]);

  useEffect(() => {
    if (editingDilution && dilutionInputRef.current) {
      dilutionInputRef.current.focus();
      dilutionInputRef.current.select();
    }
  }, [editingDilution]);

  // Shared slider — slim and refined
  const sliderSx = {
    height: 4,
    "& .MuiSlider-track": {
      height: 4,
      border: "none",
      backgroundColor: "#22c55e",
      borderRadius: 2,
    },
    "& .MuiSlider-rail": {
      height: 4,
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
      opacity: 1,
      borderRadius: 2,
    },
    "& .MuiSlider-thumb": {
      width: 16,
      height: 16,
      backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
      border: "2px solid #22c55e",
      "&:hover": {
        boxShadow: "0 0 0 8px rgba(34, 197, 94, 0.12)",
      },
      "&:active": {
        boxShadow: "0 0 0 10px rgba(34, 197, 94, 0.18)",
      },
    },
    "& .MuiSlider-valueLabel": {
      backgroundColor: isDark ? "#1a1a1a" : "#1a1a1a",
      color: "#ffffff",
      fontSize: "0.75rem",
      fontWeight: 600,
      borderRadius: 1,
      padding: "3px 7px",
    },
  };

  // Text field for inline editing
  const textFieldSx = {
    width: 62,
    "& .MuiOutlinedInput-root": {
      fontSize: "0.875rem",
      height: 28,
      "& fieldset": { borderColor: "#22c55e" },
      "&:hover fieldset": { borderColor: "#22c55e" },
      "&.Mui-focused fieldset": { borderColor: "#22c55e" },
    },
    "& .MuiOutlinedInput-input": {
      textAlign: "right",
      padding: "4px 6px",
      color: "#22c55e",
      fontWeight: 700,
    },
  };

  // Mobile slider — slightly taller for touch
  const mobileSliderSx = {
    height: 4,
    "& .MuiSlider-track": {
      height: 4,
      border: "none",
      backgroundColor: "#22c55e",
      borderRadius: 2,
    },
    "& .MuiSlider-rail": {
      height: 4,
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
      opacity: 1,
      borderRadius: 2,
    },
    "& .MuiSlider-thumb": {
      width: 18,
      height: 18,
      backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
      border: "2px solid #22c55e",
      "&:hover": {
        boxShadow: "0 0 0 8px rgba(34, 197, 94, 0.12)",
      },
    },
  };

  const mobileTextFieldSx = {
    width: 68,
    "& .MuiOutlinedInput-root": {
      fontSize: "0.875rem",
      height: 32,
      "& fieldset": { borderColor: "#22c55e" },
      "&:hover fieldset": { borderColor: "#22c55e" },
      "&.Mui-focused fieldset": { borderColor: "#22c55e" },
    },
    "& .MuiOutlinedInput-input": {
      textAlign: "right",
      padding: "4px 8px",
      color: "#22c55e",
      fontWeight: 700,
    },
  };

  // Shared card wrapper styles
  const cardSx = {
    borderRadius: 2,
    backgroundColor: isDark ? '#212121' : '#f5f5f5',
    border: isDark
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  };

  // Anchors the rail below the sticky page header (64px) so it stays in view
  // while the table/charts scroll. Falls back to a self-contained scroll if
  // the parameters are taller than the viewport.
  const stickyRailSx = {
    position: "sticky" as const,
    top: 80,
    alignSelf: "flex-start" as const,
    maxHeight: "calc(100vh - 96px)",
    overflowY: "auto" as const,
  };

  // Section header strip (top of card)
  const headerSx = {
    display: "flex",
    alignItems: "center",
    px: 2,
    py: 1,
    borderBottom: isDark
      ? "1px solid rgba(255, 255, 255, 0.07)"
      : "1px solid rgba(0, 0, 0, 0.07)",
  };

  // Group divider between parameters
  const groupDividerSx = {
    borderTop: isDark
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid rgba(0, 0, 0, 0.06)",
  };

  // Param group label (uppercase, dim) — larger on mobile for legibility
  const paramLabelSx = {
    fontSize: isMobile ? "0.8rem" : "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    color: isDark ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.85)",
  };

  // Historical reference text ("3-yr avg" / "Current") — larger on mobile
  const historicalLabelSx = {
    fontSize: isMobile ? "0.8rem" : "0.65rem",
    color: isDark ? "rgba(255, 255, 255, 0.28)" : "rgba(0, 0, 0, 0.28)",
    mt: isMobile ? 0.5 : 0.25,
  };

  // Clickable value display
  const valueSx = {
    fontSize: isMobile ? "1.375rem" : "1.125rem",
    fontWeight: 700,
    color: "#22c55e",
    cursor: "pointer",
    lineHeight: 1,
    "&:hover": { opacity: 0.75 },
  };

  // Restores every slider to its loaded baseline assumption and clears any
  // manual per-year cell edits. Always available — pressing it with nothing
  // changed is a harmless no-op.
  const resetButton = (
    <Box sx={{ ...groupDividerSx, px: isMobile ? 2 : 2.5, py: isMobile ? 2 : 1.5 }}>
      <Button
        onClick={onReset}
        size="small"
        startIcon={<RestartAltIcon sx={{ fontSize: isMobile ? 20 : 14 }} />}
        sx={{
          textTransform: "none",
          fontSize: isMobile ? "0.95rem" : "0.7rem",
          fontWeight: 600,
          color: isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
          minWidth: 0,
          py: isMobile ? 1.1 : 0.4,
          px: isMobile ? 2.5 : 1,
          borderRadius: 1,
          border: isDark
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
          "&:hover": {
            color: "#22c55e",
            borderColor: "#22c55e",
            backgroundColor: isDark ? "rgba(34, 197, 94, 0.08)" : "rgba(34, 197, 94, 0.06)",
          },
        }}
      >
        Reset
      </Button>
    </Box>
  );

  // ─── Mobile layout ────────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <Box sx={{ ...cardSx, width: "100%" }}>
        {/* Header */}
        <Box sx={{ ...headerSx, justifyContent: "center", py: 1.5 }}>
          <Typography sx={{ ...paramLabelSx, fontSize: "1.05rem" }}>Parameters</Typography>
        </Box>

        {/* Parameter groups stacked */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Revenue Growth */}
          <Box sx={{ px: 2, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={paramLabelSx}>Revenue Growth</Typography>
                  <Tooltip
                    title={
                      <Typography variant="caption">Expected annual revenue growth rate</Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 12, color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)", cursor: "help" }} />
                  </Tooltip>
                </Box>
                {assumptions && (
                  <Typography sx={historicalLabelSx}>
                    3-yr avg: {((assumptions.revenueGrowthSmoothed ?? 0) * 100).toFixed(1)}%
                  </Typography>
                )}
              </Box>
              {editingRevenueGrowth ? (
                <TextField
                  inputRef={revenueGrowthInputRef}
                  value={revenueGrowthInput}
                  onChange={(e) => setRevenueGrowthInput(e.target.value)}
                  onBlur={handleSaveRevenueGrowth}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveRevenueGrowth();
                    if (e.key === "Escape") setEditingRevenueGrowth(false);
                  }}
                  size="small"
                  InputProps={{ endAdornment: "%" }}
                  sx={mobileTextFieldSx}
                />
              ) : (
                <Typography onClick={handleStartEditRevenueGrowth} sx={valueSx}>
                  {((revenueGrowth ?? 0) * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>
            <Slider
              value={(revenueGrowth ?? 0) * 100}
              onChange={(_, value) => setRevenueGrowth((value as number) / 100)}
              min={-10}
              max={200}
              step={0.5}
              sx={mobileSliderSx}
            />
          </Box>

          {/* Net Income Margin */}
          <Box sx={{ ...groupDividerSx, px: 2, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={paramLabelSx}>Net Income Margin</Typography>
                  <Tooltip
                    title={
                      <Typography variant="caption">Revenue × Margin = Net Income</Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 12, color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)", cursor: "help" }} />
                  </Tooltip>
                </Box>
                {assumptions && (
                  <Typography sx={historicalLabelSx}>
                    Current: {((assumptions.netIncomeMargin ?? 0) * 100).toFixed(1)}%
                  </Typography>
                )}
              </Box>
              {editingNetIncomeMargin ? (
                <TextField
                  inputRef={netIncomeMarginInputRef}
                  value={netIncomeMarginInput}
                  onChange={(e) => setNetIncomeMarginInput(e.target.value)}
                  onBlur={handleSaveNetIncomeMargin}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveNetIncomeMargin();
                    if (e.key === "Escape") setEditingNetIncomeMargin(false);
                  }}
                  size="small"
                  InputProps={{ endAdornment: "%" }}
                  sx={mobileTextFieldSx}
                />
              ) : (
                <Typography onClick={handleStartEditNetIncomeMargin} sx={valueSx}>
                  {((netIncomeMargin ?? 0) * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>
            <Slider
              value={(netIncomeMargin ?? 0) * 100}
              onChange={(_, value) => setNetIncomeMargin((value as number) / 100)}
              min={-20}
              max={100}
              step={0.5}
              sx={mobileSliderSx}
            />
          </Box>

          {/* PE Ratio */}
          <Box sx={{ ...groupDividerSx, px: 2, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={paramLabelSx}>PE Ratio</Typography>
                  <Tooltip
                    title={
                      <Typography variant="caption">Price-to-Earnings multiple (Low = PE, High = PE × 1.5)</Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 12, color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)", cursor: "help" }} />
                  </Tooltip>
                </Box>
                {assumptions && (
                  <Typography sx={historicalLabelSx}>
                    Current: {(assumptions.peRatio ?? 0).toFixed(1)}
                  </Typography>
                )}
              </Box>
              {editingPeRatio ? (
                <TextField
                  inputRef={peRatioInputRef}
                  value={peRatioInput}
                  onChange={(e) => setPeRatioInput(e.target.value)}
                  onBlur={handleSavePeRatio}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSavePeRatio();
                    if (e.key === "Escape") setEditingPeRatio(false);
                  }}
                  size="small"
                  sx={{ ...mobileTextFieldSx, width: 60 }}
                />
              ) : (
                <Typography onClick={handleStartEditPeRatio} sx={valueSx}>
                  {(peRatio ?? 15).toFixed(1)}
                </Typography>
              )}
            </Box>
            <Slider
              value={peRatio ?? 15}
              onChange={(_, value) => setPeRatio(value as number)}
              min={1}
              max={100}
              step={0.5}
              sx={mobileSliderSx}
            />
          </Box>

          {/* Annual Dilution */}
          <Box sx={{ ...groupDividerSx, px: 2, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography sx={paramLabelSx}>Annual Dilution</Typography>
                  <Tooltip
                    title={
                      <Typography variant="caption">
                        Annual share dilution (positive = new shares issued, negative = buybacks). Compounds each year.
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 12, color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)", cursor: "help" }} />
                  </Tooltip>
                </Box>
                {assumptions && (
                  <Typography sx={historicalLabelSx}>
                    3-yr avg: {((assumptions.sharesGrowthSmoothed ?? 0) * 100).toFixed(1)}%
                  </Typography>
                )}
              </Box>
              {editingDilution ? (
                <TextField
                  inputRef={dilutionInputRef}
                  value={dilutionInput}
                  onChange={(e) => setDilutionInput(e.target.value)}
                  onBlur={handleSaveDilution}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveDilution();
                    if (e.key === "Escape") setEditingDilution(false);
                  }}
                  size="small"
                  InputProps={{ endAdornment: "%" }}
                  sx={mobileTextFieldSx}
                />
              ) : (
                <Typography onClick={handleStartEditDilution} sx={valueSx}>
                  {((dilutionRate ?? 0) * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>
            <Slider
              value={(dilutionRate ?? 0) * 100}
              onChange={(_, value) => setDilutionRate((value as number) / 100)}
              min={-20}
              max={30}
              step={0.5}
              sx={mobileSliderSx}
            />
          </Box>

          {resetButton}
        </Box>
      </Box>
    );
  }

  // ─── Collapsed state ──────────────────────────────────────────────────────────

  if (isCollapsed) {
    return (
      <Box
        sx={{
          ...cardSx,
          ...stickyRailSx,
          width: COLLAPSED_WIDTH,
          minWidth: COLLAPSED_WIDTH,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "fit-content",
          py: 1.5,
          transition: "width 0.2s ease-in-out",
        }}
      >
        <Tooltip title="Show parameters" arrow placement="right">
          <IconButton
            onClick={() => setIsCollapsed(false)}
            size="small"
            sx={{
              width: 26,
              height: 26,
              color: isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.4)",
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Projection parameters" arrow placement="right">
          <Box sx={{ mt: 1.5 }}>
            <TuneIcon
              sx={{
                fontSize: 18,
                color: isDark ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.3)",
              }}
            />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  // ─── Expanded desktop state ───────────────────────────────────────────────────

  return (
    <Box
      sx={{
        ...cardSx,
        ...stickyRailSx,
        width: EXPANDED_WIDTH,
        minWidth: EXPANDED_WIDTH,
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        transition: "width 0.2s ease-in-out",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.75,
          borderBottom: isDark
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          Parameters
        </Typography>

        <Tooltip title="Hide panel" arrow placement="bottom">
          <IconButton
            onClick={() => setIsCollapsed(true)}
            size="small"
            sx={{
              width: 22,
              height: 22,
              color: isDark ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.35)",
              "&:hover": {
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Parameter groups */}
      <Box>
        {/* Revenue Growth */}
        <Box sx={{ px: 2.5, py: 2.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={paramLabelSx}>Revenue Growth</Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                        Expected annual revenue growth rate
                      </Typography>
                      {assumptions && (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.65, fontSize: "0.7rem" }}>
                          3-yr avg: {((assumptions.revenueGrowthSmoothed ?? 0) * 100).toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 12,
                      color: isDark ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.25)",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              {assumptions && (
                <Typography sx={historicalLabelSx}>
                  3-yr avg: {((assumptions.revenueGrowthSmoothed ?? 0) * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>

            {editingRevenueGrowth ? (
              <TextField
                inputRef={revenueGrowthInputRef}
                value={revenueGrowthInput}
                onChange={(e) => setRevenueGrowthInput(e.target.value)}
                onBlur={handleSaveRevenueGrowth}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveRevenueGrowth();
                  if (e.key === "Escape") setEditingRevenueGrowth(false);
                }}
                size="small"
                InputProps={{ endAdornment: "%" }}
                sx={textFieldSx}
              />
            ) : (
              <Typography onClick={handleStartEditRevenueGrowth} sx={valueSx}>
                {((revenueGrowth ?? 0) * 100).toFixed(1)}%
              </Typography>
            )}
          </Box>

          <Slider
            value={(revenueGrowth ?? 0) * 100}
            onChange={(_, value) => setRevenueGrowth((value as number) / 100)}
            min={-10}
            max={200}
            step={0.5}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${(value ?? 0).toFixed(1)}%`}
            sx={sliderSx}
          />
        </Box>

        {/* Net Income Margin */}
        <Box sx={{ ...groupDividerSx, px: 2.5, py: 2.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={paramLabelSx}>Net Income Margin</Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                        Revenue × Margin = Net Income
                      </Typography>
                      {assumptions && (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.65, fontSize: "0.7rem" }}>
                          Current: {((assumptions.netIncomeMargin ?? 0) * 100).toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 12,
                      color: isDark ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.25)",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              {assumptions && (
                <Typography sx={historicalLabelSx}>
                  Current: {((assumptions.netIncomeMargin ?? 0) * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>

            {editingNetIncomeMargin ? (
              <TextField
                inputRef={netIncomeMarginInputRef}
                value={netIncomeMarginInput}
                onChange={(e) => setNetIncomeMarginInput(e.target.value)}
                onBlur={handleSaveNetIncomeMargin}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveNetIncomeMargin();
                  if (e.key === "Escape") setEditingNetIncomeMargin(false);
                }}
                size="small"
                InputProps={{ endAdornment: "%" }}
                sx={textFieldSx}
              />
            ) : (
              <Typography onClick={handleStartEditNetIncomeMargin} sx={valueSx}>
                {((netIncomeMargin ?? 0) * 100).toFixed(1)}%
              </Typography>
            )}
          </Box>

          <Slider
            value={(netIncomeMargin ?? 0) * 100}
            onChange={(_, value) => setNetIncomeMargin((value as number) / 100)}
            min={-20}
            max={100}
            step={0.5}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${(value ?? 0).toFixed(1)}%`}
            sx={sliderSx}
          />
        </Box>

        {/* PE Ratio */}
        <Box sx={{ ...groupDividerSx, px: 2.5, py: 2.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={paramLabelSx}>PE Ratio</Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                        Price-to-Earnings multiple (Low = PE, High = PE × 1.5)
                      </Typography>
                      {assumptions && (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.65, fontSize: "0.7rem" }}>
                          Current: {(assumptions.peRatio ?? 0).toFixed(1)}
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 12,
                      color: isDark ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.25)",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              {assumptions && (
                <Typography sx={historicalLabelSx}>
                  Current: {(assumptions.peRatio ?? 0).toFixed(1)}
                </Typography>
              )}
            </Box>

            {editingPeRatio ? (
              <TextField
                inputRef={peRatioInputRef}
                value={peRatioInput}
                onChange={(e) => setPeRatioInput(e.target.value)}
                onBlur={handleSavePeRatio}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSavePeRatio();
                  if (e.key === "Escape") setEditingPeRatio(false);
                }}
                size="small"
                sx={{ ...textFieldSx, width: 55 }}
              />
            ) : (
              <Typography onClick={handleStartEditPeRatio} sx={valueSx}>
                {(peRatio ?? 15).toFixed(1)}
              </Typography>
            )}
          </Box>

          <Slider
            value={peRatio ?? 15}
            onChange={(_, value) => setPeRatio(value as number)}
            min={1}
            max={100}
            step={0.5}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => (value ?? 15).toFixed(1)}
            sx={sliderSx}
          />
        </Box>

        {/* Annual Dilution */}
        <Box sx={{ ...groupDividerSx, px: 2.5, py: 2.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={paramLabelSx}>Annual Dilution</Typography>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                        Annual share dilution (positive = new shares issued, negative = buybacks). Compounds each year.
                      </Typography>
                      {assumptions && (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.65, fontSize: "0.7rem" }}>
                          3-yr avg: {((assumptions.sharesGrowthSmoothed ?? 0) * 100).toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 12,
                      color: isDark ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.25)",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              {assumptions && (
                <Typography sx={historicalLabelSx}>
                  3-yr avg: {((assumptions.sharesGrowthSmoothed ?? 0) * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>

            {editingDilution ? (
              <TextField
                inputRef={dilutionInputRef}
                value={dilutionInput}
                onChange={(e) => setDilutionInput(e.target.value)}
                onBlur={handleSaveDilution}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveDilution();
                  if (e.key === "Escape") setEditingDilution(false);
                }}
                size="small"
                InputProps={{ endAdornment: "%" }}
                sx={textFieldSx}
              />
            ) : (
              <Typography onClick={handleStartEditDilution} sx={valueSx}>
                {((dilutionRate ?? 0) * 100).toFixed(1)}%
              </Typography>
            )}
          </Box>

          <Slider
            value={(dilutionRate ?? 0) * 100}
            onChange={(_, value) => setDilutionRate((value as number) / 100)}
            min={-20}
            max={30}
            step={0.5}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${(value ?? 0).toFixed(1)}%`}
            sx={sliderSx}
          />
        </Box>

        {resetButton}
      </Box>
    </Box>
  );
};

export default ProjectionsSidebar;
